import { FunctionComponent, useState, useEffect } from 'react';
import styles from './PickDropHourSelector.module.scss';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { ExpandableSelectorItem, ExpandableSelectorList } from 'components/UI/V3/ExpandableSelector/List';
import moment, { Moment } from 'moment-timezone';
import { useMarketplace } from 'hooks';

interface PickDropHourSelectorProps {
    from: Moment;
    to: Moment;
    onSelect: (from: Moment, to: Moment) => void;
    className?: string;
}

const getDateFormatted = (date: Moment) => {
    const minutes = date.minutes();
    const dateFormatted = date;

    if (minutes > 0 && minutes <= 15) {
        dateFormatted.add(15 - minutes, 'minutes');
    } else if (minutes > 15 && minutes <= 30) {
        dateFormatted.add(30 - minutes, 'minutes');
    } else if (minutes > 30 && minutes <= 45) {
        dateFormatted.add(45 - minutes, 'minutes');
    } else if (minutes > 45 && minutes <= 59) {
        dateFormatted.add(60 - minutes, 'minutes');
    }

    return dateFormatted.format('h:mmA');
};

export const PickDropHourSelector: FunctionComponent<PickDropHourSelectorProps> = ({
    from,
    to,
    onSelect,
    className
}) => {
    const marketplace = useMarketplace();
    const [dropOff, setDropOff] = useState<string>(getDateFormatted(from));
    const [pickUp, setPickUp] = useState<string>(getDateFormatted(to));
    const [expandedDrop, setExpandedDrop] = useState(false);
    const [expandedPick, setExpandedPick] = useState(false);

    useEffect(() => {
        setDropOff(getDateFormatted(from));
    }, [from]);

    useEffect(() => {
        setPickUp(getDateFormatted(to));
    }, [to]);

    const generateTimeIntervals = (): string[] => {
        const intervals: string[] = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const date = new Date();
                date.setHours(hour, minute, 0, 0);
                const timeString = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
                intervals.push(timeString);
            }
        }
        return intervals;
    };

    const timeIntervals = generateTimeIntervals();

    const handleDropOffChange = (selectedHour: string) => {
        setExpandedDrop(false);
        const fromDate = moment.tz(
            `${from.format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        onSelect(fromDate, to);
    };

    const handlePickUpChange = (selectedHour: string) => {
        setExpandedPick(false);
        const toDate = moment.tz(
            `${to.format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        onSelect(from, toDate);
    };

    return (
        <div className={styles.container}>
            <div className={styles.optionWrapper}>
                <span className={styles.text}>Drop-Off</span>
                <span className={styles.time}>{dropOff.toLowerCase()}</span>
                <ExpandableSelector
                    id="drop-off-selector"
                    expanded={expandedDrop}
                    onExpand={() => setExpandedDrop(!expandedDrop)}
                    onClickAway={() => setExpandedDrop(false)}
                    className={styles.timeSelector}
                    childrenClassName={styles.list}
                    dropdownAlign="right"
                >
                    <ExpandableSelectorList>
                        {timeIntervals.map(time => (
                            <ExpandableSelectorItem
                                key={time}
                                selected={dropOff === time}
                                onClick={() => handleDropOffChange(time)}
                            >
                                {time}
                            </ExpandableSelectorItem>
                        ))}
                    </ExpandableSelectorList>
                </ExpandableSelector>

                <span className={styles.text}>Pick-Up</span>
                <span className={styles.time}>{pickUp.toLowerCase()}</span>
                <ExpandableSelector
                    id="pick-up-selector"
                    expanded={expandedPick}
                    onExpand={() => setExpandedPick(!expandedPick)}
                    onClickAway={() => setExpandedPick(false)}
                    className={styles.timeSelector}
                    childrenClassName={styles.list}
                    dropdownAlign="right"
                >
                    <ExpandableSelectorList>
                        {timeIntervals.map(time => (
                            <ExpandableSelectorItem
                                key={time}
                                selected={pickUp === time}
                                onClick={() => handlePickUpChange(time)}
                            >
                                {time}
                            </ExpandableSelectorItem>
                        ))}
                    </ExpandableSelectorList>
                </ExpandableSelector>
            </div>
        </div>
    );
};

export default PickDropHourSelector;
